import { Popover } from "antd";
import {
  ArchivedDependent,
  ArchiveTicket,
} from "../../../Types/restoreDatabaseTypes";
import {
  RestoreDBModalModes,
  RestoreDBServiceType,
  RestoreDbTypes,
  TableView,
} from "../../../enums/RestoreDatabaseEnums";
import { ColumnsType } from "antd/es/table";
import { EllipsisOutlined } from "@ant-design/icons";
import { PlacementPositions } from "../../../enums/PlacementPositionsEnums";
import styles from "./RestoreDbTable.module.scss";
import { MdDelete } from "react-icons/md";
import restoreIcon from "../../../assets/images/make-primary-document-icon.svg";
import { appRoutes } from "../../../constants/appRoutes";
import { RestorePageSizeMeta } from "../../../Types";
import { storageKeys } from "../../../constants/storageKeys";

interface GetColoumnsParams {
  activeDbView: RestoreDbTypes;
  handleAction: (
    id?: string,
    modalMode: RestoreDBModalModes,
    type?: string
  ) => void;
}

export const NO_DATA_MESSAGE = "No deleted records available";

const renderWithHyphen = (text: string) => (text ? text : "-");

export const tab1label = {
  [RestoreDbTypes.CUSTOMER]: "Customers",
  [RestoreDbTypes.OWNER_OPERATOR]: "Operators",
  [RestoreDbTypes.QUARRY]: "Quarries",
  [RestoreDbTypes.JOB]: "Jobs",
};

export const firstColumnTitle = {
  [RestoreDbTypes.CUSTOMER]: "CUSTOMER NAME",
  [RestoreDbTypes.OWNER_OPERATOR]: "OPERATOR NAME",
  [RestoreDbTypes.CDL_DRIVER]: "DRIVER NAME",
  [RestoreDbTypes.QUARRY]: "QUARRY NAME",
  [RestoreDbTypes.JOB]: "JOB NAME",
};

export const restoreDbTypes = {
  [RestoreDbTypes.CUSTOMER]: RestoreDBServiceType.CUSTOMER,
  [RestoreDbTypes.OWNER_OPERATOR]: RestoreDBServiceType.OWNER_OPERATOR,
  [RestoreDbTypes.CDL_DRIVER]: RestoreDBServiceType.CDL_DRIVER,
  [RestoreDbTypes.QUARRY]: RestoreDBServiceType.QUARRY,
  [RestoreDbTypes.JOB]: RestoreDBServiceType.JOB,
};

export const getRestoreDBParams = (
  activeDbView: RestoreDbTypes,
  activeView: TableView,
  currentPage: number,
  pageSize: number
) => {
  if (activeDbView === RestoreDbTypes.CDL_DRIVER) {
    return {
      type: restoreDbTypes[activeDbView],
      page: currentPage,
      perPage: pageSize,
    };
  }
  return {
    type: restoreDbTypes[activeDbView],
    dependent: activeView === TableView.DEPENDENT ? true : undefined,
    page: currentPage,
    perPage: pageSize,
  };
};

export const tabLabels = (activeView: RestoreDbTypes) => {
  return {
    TAB_1_LABEL: `Archived ${tab1label[activeView]}`,
    TAB_2_LABEL: `Archived ${activeView === RestoreDbTypes.QUARRY ? "Materials," : ""} Contacts, Notes & Documents`,
  };
};

export const getColumns = (
  params?: GetColoumnsParams
): ColumnsType<ArchiveTicket> => {
  const { activeDbView, handleAction } = params;
  const firstColumnsName = firstColumnTitle[activeDbView];
  return [
    {
      title: firstColumnsName,
      key: "name",
      align: "center",
      dataIndex: "name",
      render: renderWithHyphen,
    },
    {
      title: "DELETED BY",
      key: "deletedBy",
      align: "center",
      dataIndex: "deletedBy",
      render: renderWithHyphen,
    },
    {
      title: "ROLE",
      key: "role",
      align: "center",
      dataIndex: "role",
      render: renderWithHyphen,
    },
    {
      title: "DELETED DATE",
      key: "deletedDate",
      align: "center",
      dataIndex: "deletedDate",
      render: renderWithHyphen,
    },
    {
      title: "NOTES",
      key: "notes",
      align: "center",
      dataIndex: "notes",
      render: renderWithHyphen,
    },
    {
      title: "",
      key: "actions",
      align: "center",
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => {
        return (
          <Popover
            placement={PlacementPositions.LEFT}
            content={
              <>
                <div
                  className={styles.PopoverContent}
                  onClick={() => {
                    handleAction(record?.id, RestoreDBModalModes.DELETE);
                  }}
                >
                  <MdDelete />
                  Delete
                </div>
                <div
                  className={styles.PopoverContent}
                  onClick={() => {
                    handleAction(record?.id, RestoreDBModalModes.RESTORE);
                  }}
                >
                  <img src={restoreIcon} />
                  Restore
                </div>
              </>
            }
          >
            <EllipsisOutlined
              style={{ fontSize: "30px", color: "#C3C8CD" }}
              className="action-ellipsis-button"
            />
          </Popover>
        );
      },
    },
  ];
};

export const getColumnsDependent = (
  params?: GetColoumnsParams
): ColumnsType<ArchivedDependent> => {
  const { activeDbView, handleAction } = params;
  const firstColumnsName = firstColumnTitle[activeDbView];
  return [
    {
      title: firstColumnsName,
      dataIndex: "name",
      key: "name",
      align: "center",
      render: renderWithHyphen,
    },
    {
      title: "TYPE",
      dataIndex: "type",
      key: "type",
      align: "center",
      render: renderWithHyphen,
    },
    {
      title: "TITLE",
      dataIndex: "title",
      key: "title",
      align: "center",
      render: renderWithHyphen,
    },
    {
      title: "DELETED BY",
      dataIndex: "deletedBy",
      key: "deletedBy",
      align: "center",
      render: renderWithHyphen,
    },
    {
      title: "ROLE",
      dataIndex: "role",
      key: "role",
      align: "center",
      render: renderWithHyphen,
    },
    {
      title: "DELETED DATE",
      dataIndex: "deletedDate",
      key: "deletedDate",
      align: "center",
      render: renderWithHyphen,
    },
    {
      title: "NOTES",
      dataIndex: "notes",
      key: "notes",
      align: "center",
      render: renderWithHyphen,
    },
    {
      title: "",
      key: "actions",
      align: "center",
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => {
        return (
          <Popover
            placement={PlacementPositions.LEFT}
            content={
              <>
                <div
                  className={styles.PopoverContent}
                  onClick={() => {
                    handleAction(
                      record?.id,
                      RestoreDBModalModes.DELETE,
                      record?.type
                    );
                  }}
                >
                  <MdDelete />
                  Delete
                </div>
                <div
                  className={styles.PopoverContent}
                  onClick={() => {
                    handleAction(
                      record?.id,
                      RestoreDBModalModes.RESTORE,
                      record?.type
                    );
                  }}
                >
                  <img src={restoreIcon} />
                  Restore
                </div>
              </>
            }
          >
            <EllipsisOutlined
              style={{ padding: "0.5rem", fontSize: "30px", color: "#C3C8CD" }}
            />
          </Popover>
        );
      },
    },
  ];
};

export const modalConstants = {
  RESTORE_TITLE: "Restore",
  DELETE_TITLE: "Delete",
  MODAL_WIDTH: "700px",
  MODAL_OK_LABEL: "Confirm",
  RESTORE_CONTENT: "Are you sure you want to restore the data?",
  DELETE_CONTENT: {
    [TableView.MAIN]: {
      [RestoreDbTypes.CUSTOMER]:
        "Are you sure you want to delete this customer, once deleted it can’t be recovered again?",
      [RestoreDbTypes.OWNER_OPERATOR]:
        "Are you sure you want to delete this owner operator, once deleted it can’t be recovered again?",
      [RestoreDbTypes.CDL_DRIVER]:
        "Are you sure you want to delete this CDL driver, once deleted it can’t be recovered again?",
      [RestoreDbTypes.QUARRY]:
        "Are you sure you want to delete this quarry, once deleted it can’t be recovered again?",
      [RestoreDbTypes.JOB]:
        "Are you sure you want to delete this job, once deleted it can’t be recovered again?",
    },
    [TableView.DEPENDENT]: {
      [RestoreDbTypes.CUSTOMER]: (record) =>
        `Are you sure you want to delete this customer ${record}, Once deleted it can't be recovered again?`,
      [RestoreDbTypes.OWNER_OPERATOR]: (record) =>
        `Are you sure you want to delete this owner operator ${record}, Once deleted it can't be recovered again?`,
      [RestoreDbTypes.CDL_DRIVER]: (record) =>
        `Are you sure you want to delete this CDL driver ${record}, Once deleted it can't be recovered again?`,
      [RestoreDbTypes.QUARRY]: (record) =>
        `Are you sure you want to delete this quarry ${record}, Once deleted it can't be recovered again?`,
      [RestoreDbTypes.JOB]: (record) =>
        `Are you sure you want to delete this job ${record}, Once deleted it can't be recovered again?`,
    },
  },
  RESTORE_CONTENT: {
    [TableView.MAIN]: {
      [RestoreDbTypes.CUSTOMER]:
        "Are you sure you want to restore the customer?",
      [RestoreDbTypes.OWNER_OPERATOR]:
        "Are you sure you want to restore the owner operator?",
      [RestoreDbTypes.CDL_DRIVER]:
        "Are you sure you want to restore the CDL driver?",
      [RestoreDbTypes.QUARRY]: "Are you sure you want to restore the quarry?",
      [RestoreDbTypes.JOB]: "Are you sure you want to restore the job?",
    },
    [TableView.DEPENDENT]: {
      [RestoreDbTypes.CUSTOMER]: (record) =>
        `Are you sure you want to restore this customer ${record}?`,
      [RestoreDbTypes.OWNER_OPERATOR]: (record) =>
        `Are you sure you want to restore this owner operator ${record}?`,
      [RestoreDbTypes.CDL_DRIVER]: (record) =>
        `Are you sure you want to restore this CDL driver ${record}?`,
      [RestoreDbTypes.QUARRY]: (record) =>
        `Are you sure you want to restore this quarry ${record}?`,
      [RestoreDbTypes.JOB]: (record) =>
        `Are you sure you want to restore this job ${record}?`,
    },
  },
};

export const navigationRoutes = {
  [RestoreDbTypes.CUSTOMER]: appRoutes.CUSTOMER_PROFILE,
  [RestoreDbTypes.QUARRY]: appRoutes.QUARRY_PROFILE,
  [RestoreDbTypes.JOB]: appRoutes.JOB_PROFILE,
  [RestoreDbTypes.OWNER_OPERATOR]: appRoutes.OWNER_OPERATOR_PROFILE,
};

export const ActionMessages = {
  ARCHIVE_SUCCESS: {
    [RestoreDbTypes.CUSTOMER]: "Customer record restored successfully!",
    [RestoreDbTypes.OWNER_OPERATOR]:
      "Owner Operator record restored successfully!",
    [RestoreDbTypes.CDL_DRIVER]: "CDL Driver record restored successfully!",
    [RestoreDbTypes.QUARRY]: "Quarry record restored successfully!",
    [RestoreDbTypes.JOB]: "Job record restored successfully!",
  },
  DELETE_SUCCESS: {
    [RestoreDbTypes.CUSTOMER]: "Customer record deleted successfully!",
    [RestoreDbTypes.OWNER_OPERATOR]:
      "Owner Operator record deleted successfully!",
    [RestoreDbTypes.CDL_DRIVER]: "CDL Driver record deleted successfully!",
    [RestoreDbTypes.QUARRY]: "Quarry record deleted successfully!",
    [RestoreDbTypes.JOB]: "Job record deleted successfully!",
  },
  ARCHIVE_FAILURE: "Restore action failed. Please try again later!",
  DELETE_FAILURE: "Delete action failed. Please try again later!",
  IS_PARENT_ARCHIVED: "linked",
  RESTORE_PARENT: {
    [RestoreDbTypes.JOB]: "Please restore the Job record first.",
    [RestoreDbTypes.QUARRY]: "Please restore the Quarry record first.",
    [RestoreDbTypes.CUSTOMER]: "Please restore the Customer record first.",
    [RestoreDbTypes.OWNER_OPERATOR]:
      "Please restore the Owner Operator record first.",
  },
};

export const getStorageKey = (
  activeDBView: RestoreDbTypes,
  activeView: TableView,
) => {
  const isDependent = activeView === TableView.DEPENDENT;

  const keyMap: Record<RestoreDbTypes, { main: string; dependent: string }> = {
    [RestoreDbTypes.CUSTOMER]: {
      main: storageKeys.RESTORE_CUSTOMER_PAGESIZE,
      dependent: storageKeys.RESTORE_CUSTOMER_DEP_PAGESIZE,
    },
    [RestoreDbTypes.QUARRY]: {
      main: storageKeys.RESTORE_QUARRY_PAGESIZE,
      dependent: storageKeys.RESTORE_QUARRY_DEP_PAGESIZE,
    },
    [RestoreDbTypes.OWNER_OPERATOR]: {
      main: storageKeys.RESTORE_OWNER_PAGESIZE,
      dependent: storageKeys.RESTORE_OWNER_DEP_PAGESIZE,
    },
    [RestoreDbTypes.CDL_DRIVER]: {
      main: storageKeys.RESTORE_CDL_PAGESIZE,
      dependent: storageKeys.RESTORE_CDL_PAGESIZE,
    },
    [RestoreDbTypes.JOB]: {
      main: storageKeys.RESTORE_JOB_PAGESIZE,
      dependent: storageKeys.RESTORE_JOB_DEP_PAGESIZE,
    },
  };
  return isDependent
    ? keyMap[activeDBView].dependent
    : keyMap[activeDBView].main;
};
